import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MemberSignIn } from "@/components/bills/MemberSignIn";

const ReadOnly = ({ label, value, testId }) => (
  <div className="space-y-1">
    <Label className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">{label}</Label>
    <Input value={value || ""} readOnly data-testid={testId} className="h-10 bg-[#E5EDE3]/60 border-[#D8D2C4] text-[#1B2A41]" />
  </div>
);

const ContactForm = ({ member, onDone }) => {
  const { data: subjects = [] } = useQuery({ queryKey: ["contact-subjects"], queryFn: () => api.get("/contact/subjects").then((r) => r.data) });
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { subject, message });
      onDone(subject);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <ReadOnly label="Name" value={member.name} testId="contact-name" />
        <ReadOnly label="Electorate" value={`${member.electorate} (${member.state})`} testId="contact-electorate" />
      </div>
      <ReadOnly label="Email" value={member.email} testId="contact-email" />
      <div className="space-y-1">
        <Label htmlFor="contact-subject" className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">Subject</Label>
        <select id="contact-subject" data-testid="contact-subject-select" required value={subject} onChange={(e) => setSubject(e.target.value)} className="h-11 w-full rounded-md border border-[#D8D2C4] bg-white px-3 text-sm text-[#1B2A41]">
          <option value="">— choose a subject —</option>
          {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="contact-message" className="font-type text-[11px] uppercase tracking-wider text-[#5A7355]">Message</Label>
        <Textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us what's on your mind…" className="bg-white border-[#D8D2C4] focus-visible:ring-[#E2705A]" />
      </div>
      <button type="submit" disabled={busy || !subject} data-testid="contact-submit-button" className="w-full h-12 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 text-[#FDFBF7] font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send message
      </button>
    </form>
  );
};

export const ContactButton = ({ className = "" }) => {
  const { member } = useMember();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!open) setTimeout(() => setSent(null), 300);
  }, [open]);

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="contact-us-button" className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#8FA58A]/50 text-[#8FA58A] hover:text-[#FDFBF7] hover:border-[#E2705A] hover:bg-[#E2705A] transition-colors font-semibold text-sm ${className}`}>
        <Mail className="w-4 h-4" /> Contact us
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="contact-dialog" className="bg-[#FAF8F2] border-[#E2705A]/40 text-[#1E2830] max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-type text-[#1B2A41]">Contact the R.O.A.N team</DialogTitle>
            <DialogDescription className="text-[#5C6A79]">
              {member ? "Your details are filled in from your F.E.R.M registration." : "Contact is open to registered F.E.R.Ms. Sign in with the email you registered with — we'll send you a one-time link."}
            </DialogDescription>
          </DialogHeader>
          {sent ? (
            <div data-testid="contact-success" className="text-center py-6">
              <CheckCircle2 className="w-12 h-12 mx-auto text-[#5A7355]" />
              <p className="font-type font-bold text-xl text-[#1B2A41] mt-3">Message sent</p>
              <p className="text-sm text-[#5C6A79] mt-1">"{sent}" — we'll reply to {member?.email}.</p>
              <button onClick={() => setOpen(false)} data-testid="contact-close-button" className="mt-5 text-sm font-semibold text-[#E2705A] hover:underline">Close</button>
            </div>
          ) : member ? (
            <ContactForm member={member} onDone={setSent} />
          ) : (
            <MemberSignIn />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
